import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  WorkbookExportDialog,
  type SelectedWorkbookExport,
} from "@/core/components/workbook-export";
import { useGetBusiness } from "@/business/query";
import { getErrorMessage } from "@/core/api/errors";
import useAuth from "@/core/auth/use-auth";
import { useBusiness } from "@/core/business";
import {
  canRerunWholeWorkbook,
  canRunInitialPayroll,
  hasAiScriptAccess,
  hasIcmPayrollViewerAccess,
  hasPayrollScriptAccess,
} from "@/core/business/payroll-only";
import { useEnv } from "@/core/env";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { dayjs } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import { IcmJobSidebar } from "@/icm/common";
import {
  FOX_SONS_BUSINESS_NAME,
  getFoxSonsVisibleTechIds,
} from "@/icm/constants/fox-sons-access-control";
import { IcmPageBreakdownCard } from "@/icm/icm-page/icm-page-breakdown-card";
import { IcmPagePlanMismatchDialog } from "@/icm/icm-page/icm-page-plan-mismatch-dialog";
import { IcmPageSummaryCard } from "@/icm/icm-page/icm-page-summary-card";
import {
  buildPeriods,
  DEFAULT_TEMPLATE,
  hasActiveIcmRun,
} from "@/icm/icm-page/utils";
import { useGetIcmJobs, useGetIcmStatements } from "@/icm/query";
import { TechnicianPayrollPage } from "@/icm/technician-payroll";
import type { IcmJob } from "@/icm/types";
import { DemoAiScriptExplanationChatSidebar } from "@/payroll/ai-script/ai-script-explanation-chat/demo-ai-script-explanation-chat-sidebar";
import { CELL_FIGURE_QUESTION } from "@/payroll/ai-script/ai-script-explanation-chat/selection-context";
import { AiScriptExplanationChatSidebar } from "@/payroll/ai-script/ai-script-explanation-chat-sidebar";
import { AiScriptPersistentEditsSidebar } from "@/payroll/ai-script/ai-script-persistent-edits-panel";
import { manualEditsReviewCount } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import { AiScriptRunState } from "@/payroll/ai-script/ai-script-run-state";
import { type AiScriptSpreadsheetViewerHandle } from "@/payroll/ai-script/ai-script-spreadsheet-viewer";
import { CellCommentsSidebar } from "@/payroll/ai-script/cell-comments/cell-comments-sidebar";
import { useCellCommentThreads } from "@/payroll/ai-script/cell-comments/query";
import {
  useCreateAiScriptRun,
  useDownloadAiScriptWorkbook,
  useGetAiScriptCellOverrides,
  useGetAiScriptSpreadsheet,
} from "@/payroll/ai-script/query";
import type {
  AiScriptChatSelectionContext,
  IAiScriptEditedCellSelection,
  IAiSpreadsheetCellLocation,
} from "@/payroll/ai-script/types";
import {
  DEFAULT_AI_SCRIPT_WORKBOOK_FILENAME,
  getAiScriptWorkbookDownloadErrorMessage,
  getAiScriptPayrollRunStatus,
  hasActiveAiScriptRun,
} from "@/payroll/ai-script/utils";
import { ApprovalRemovalDialog } from "@/payroll/approvals/approval-removal-dialog";
import { useSheetApprovalActions } from "@/payroll/approvals/use-sheet-approval-actions";
import {
  PAYROLL_RUN_POLL_INTERVAL,
  PAYROLL_RUN_STATUS,
} from "@/payroll/constants";
import { useGetManagedPackThreads } from "@/payroll/pack-chat/manager-query";
import {
  PackChatHeaderActions,
  PackChatSectionProvider,
} from "@/payroll/pack-chat/pack-chat-section";
import { PACK_CHAT_STATUS } from "@/payroll/pack-chat/types";
import { getPayBreakdownPath } from "@/payroll/pay-breakdown/routes";
import { PayPeriodCard } from "@/payroll/pay-periods/pay-period-card";
import {
  canLockPayPeriod,
  LOCKED_RERUN_REASON,
} from "@/payroll/pay-periods/pay-period-lock";
import {
  PAYROLL_RUN_TYPE,
  useCreatePayPeriod,
  useGetPayPeriodDetail,
  useGetPayPeriods,
  useTriggerPayrollRun,
} from "@/payroll/pay-periods/query";
import type { EnrichedPayPeriod } from "@/payroll/payroll.d";
import { useCanViewPayrollRoster } from "@/payroll/roster/permissions";
import { usePayrollRosterVersion } from "@/payroll/roster/query";
import { downloadFile } from "@/payroll/utils/download-file";
import {
  createCalculatedPayPeriod,
  isPayPeriodLocked,
} from "@/payroll/utils/period-helpers";
import { isTech, mayAnswerPackQuestions } from "@/user/utils";
import { AiScriptRerunConfirmationDialog } from "./ai-script-rerun-confirmation-dialog";
import { LegacyScriptOutputState } from "./legacy-script-output-state";
import { PayBreakdownAdjustmentsTab } from "./pay-breakdown-adjustments-tab";
import { PayBreakdownCalculationLoadingState } from "./pay-breakdown-calculation-loading-state";
import { PayBreakdownEmailsTab } from "./pay-breakdown-emails-tab";
import { PayBreakdownHeader } from "./pay-breakdown-header";
import { PayBreakdownPackChatTab } from "./pay-breakdown-pack-chat-tab";
import { PayBreakdownPaneShell } from "./pay-breakdown-pane-shell";
import { PayBreakdownPayPlanTab } from "./pay-breakdown-pay-plan-tab";
import { PayBreakdownRosterTab } from "./pay-breakdown-roster-tab";
import { PayBreakdownSectionTabs } from "./pay-breakdown-section-tabs";
import { PayBreakdownSpreadsheetViewer } from "./pay-breakdown-spreadsheet-viewer";
import { usePayPlanAccess } from "./pay-plan-access";
import {
  usePayrollEmailsAccess,
  usePaypacksAccess,
} from "./payroll-emails-access";
import { PAYROLL_GRID_CONTAINER_CLASS_NAME } from "./payroll-grid-layout";
import {
  PacksHeaderActions,
  PacksSectionProvider,
  PacksSidebar,
} from "./payroll-statement-notifications/packs-section";
import { getRerunUnits } from "./rerun-units";
import {
  canRequestSheetApprovals,
  RequestApprovalAction,
  useGetSheetApprovalRequestState,
} from "./sheet-approval-requests";
import {
  getStAdjustmentsAccess,
  ST_ADJUSTMENTS_ACCESS,
  StAdjustmentsHeaderActions,
  StAdjustmentsPreviewHeaderActions,
  StAdjustmentsSectionProvider,
  StAdjustmentsSidebar,
  useStAdjustmentsProductAccess,
} from "./st-adjustments";
import {
  PAYROLL_EMAILS_ACCESS,
  PAY_BREAKDOWN_CALCULATION_STATE,
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
  PAY_BREAKDOWN_SECTION,
  type AiScriptRerunRunOptions,
} from "./types";
import { useManualEditsSidebar } from "./use-manual-edits-sidebar";
import { usePayBreakdownSection } from "./use-pay-breakdown-section";
import {
  applyPayBreakdownStatusToPeriods,
  getLatestCompletedLegacyRun,
  getDefaultPayBreakdownPeriod,
  getLegacyRunFilename,
  getPayBreakdownCalculationState,
  getPayBreakdownLatestRunAt,
  getPayBreakdownOutput,
  getPayBreakdownRerunAction,
  getPayBreakdownRunTarget,
  getPayBreakdownVersionContext,
  hasActiveAiRunAffectingVisibleSheets,
  isFullWorkbookRun,
} from "./utils";
import { hasFullWorkbookAuthority } from "./workbook-authority";

const PayBreakdownPage = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { periodStart } = useParams<{ periodStart: string }>();
  const { businessId } = useBusiness();
  const { user } = useAuth();
  const { appEnv } = useEnv();

  const [selectedJob, setSelectedJob] = useState<IcmJob | null>(null);
  const [isSectionSidebarOpen, setSectionSidebarOpen] = useState(false);
  const sectionSidebarCloseRef = useRef<(() => void) | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isExplanationChatOpen, setIsExplanationChatOpen] = useState(false);
  const [isWorkbookExportOpen, setIsWorkbookExportOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const {
    isOpen: isManualEditsOpen,
    focusedEdit,
    close: closeManualEdits,
    openList: openManualEditsList,
    openEdit: openManualEdit,
    focusEdit: focusManualEdit,
    showAll: showAllManualEdits,
    handleSelectionChange: handleEditedCellSelectionChange,
  } = useManualEditsSidebar();
  // Mirrors the spreadsheet viewer's fullscreen state so the sidebar can lay out
  // flush beside the fullscreen overlay (see the SimpleSidebar className below).
  const [isSheetMaximized, setIsSheetMaximized] = useState(false);
  const [aiScriptRerunConfirmOpen, setAiScriptRerunConfirmOpen] =
    useState(false);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const [selectionContext, setSelectionContext] =
    useState<AiScriptChatSelectionContext | null>(null);
  const spreadsheetViewerRef = useRef<AiScriptSpreadsheetViewerHandle | null>(
    null,
  );
  const selectedPeriodStart = periodStart;

  const { data: business, isLoading: isBusinessLoading } =
    useGetBusiness(businessId);
  const { data: payPeriods = [], isLoading: isPayPeriodsLoading } =
    useGetPayPeriods(businessId, {
      // Keep detecting runs started by other users while this page is open,
      // including when the period was idle when the grid first loaded.
      refetchInterval: PAYROLL_RUN_POLL_INTERVAL,
    });

  const selectedPayPeriod = payPeriods.find(
    (period) => period.start_date === selectedPeriodStart,
  );
  const isLocked = isPayPeriodLocked(selectedPayPeriod);
  const showLockPayPeriod = canLockPayPeriod({
    period: selectedPayPeriod,
    business,
    user,
  });
  const selectedAiRunId =
    selectedPayPeriod?.latest_completed_ai_script_run_id ?? null;
  const sheetApprovalActions = useSheetApprovalActions({
    businessId,
    periodId: selectedPayPeriod?.id,
    runId: selectedAiRunId,
  });
  const generatedWorkbookMetadata =
    selectedPayPeriod?.latest_completed_ai_script_run_generated_workbook_metadata ??
    null;
  const hasGeneratedWorkbook =
    !!selectedPayPeriod?.latest_completed_ai_script_run_generated_workbook_url ||
    !!generatedWorkbookMetadata;
  const selectedIcmRunId =
    selectedPayPeriod?.latest_completed_icm_run_id ?? null;

  const shouldFetchLegacyDetail =
    !!businessId &&
    !!selectedPayPeriod?.id &&
    !hasGeneratedWorkbook &&
    !selectedIcmRunId &&
    selectedPayPeriod.runs_count > 0;
  // The period SHOW endpoint is the only source of sheet approval status — the
  // index deliberately omits it (it is requester-filtered and fingerprints the
  // workbook). So fetch the detail whenever there is a generated workbook to
  // approve, on top of the legacy-output case that already needed it. Keyed by
  // the run, so a rerun that retracted approvals server-side lands on its own.
  const { data: periodDetail, isLoading: isLegacyDetailLoading } =
    useGetPayPeriodDetail(businessId, selectedPayPeriod?.id, {
      enabled: shouldFetchLegacyDetail || (!!businessId && !!selectedAiRunId),
      runId: selectedAiRunId,
    });
  const approvalStatus = periodDetail?.approval_status ?? null;
  const latestLegacyRun = getLatestCompletedLegacyRun(
    shouldFetchLegacyDetail ? periodDetail?.payroll_runs : undefined,
  );

  const output = getPayBreakdownOutput({
    hasGeneratedWorkbook,
    selectedAiRunId,
    selectedIcmRunId,
    latestLegacyRun,
  });
  const aiScriptRunId =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript
      ? output.runId
      : null;

  const { data: commentThreads } = useCellCommentThreads(
    businessId,
    aiScriptRunId,
  );
  const openCommentCount =
    commentThreads?.filter((thread) => !thread.resolved_at).length ?? 0;
  const { data: cellOverridesData } = useGetAiScriptCellOverrides(
    businessId,
    aiScriptRunId,
  );
  const manualEditsToReview = manualEditsReviewCount(
    cellOverridesData?.overrides ?? [],
  );

  const canRequestApprovals = canRequestSheetApprovals(business, user);
  const { data: sheetApprovalState } = useGetSheetApprovalRequestState(
    businessId,
    aiScriptRunId,
    canRequestApprovals,
  );

  useEffect(() => {
    if (output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript) return;
    setIsExplanationChatOpen(false);
    setIsCommentsOpen(false);
  }, [output.type]);

  useEffect(() => {
    closeManualEdits();
    setSelectionContext(null);
    setPendingQuestion(null);
  }, [aiScriptRunId, closeManualEdits]);

  const selectedIcmOutputRunId =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm ? output.runId : null;
  const {
    data: statements,
    isLoading: isStatementsLoading,
    refetch: refetchStatements,
  } = useGetIcmStatements(businessId, selectedIcmOutputRunId);
  const { data: jobsData, refetch: refetchJobs } = useGetIcmJobs(
    businessId,
    selectedIcmOutputRunId,
  );
  const jobs = jobsData?.jobs ?? [];

  const isFoxAndSons = business?.name === FOX_SONS_BUSINESS_NAME;
  const foxSonsVisibleTechIds = useMemo(
    () => (isFoxAndSons ? getFoxSonsVisibleTechIds(user?.email ?? "") : null),
    [isFoxAndSons, user?.email],
  );

  const visibleStatements = useMemo(() => {
    const all = statements ?? [];
    if (!foxSonsVisibleTechIds) return all;

    return all.filter(
      (statement) =>
        statement.technician_crm_external_id != null &&
        foxSonsVisibleTechIds.has(statement.technician_crm_external_id),
    );
  }, [statements, foxSonsVisibleTechIds]);

  const visibleJobs = useMemo(() => {
    if (!foxSonsVisibleTechIds) return jobs;

    const allowedStatementIds = new Set(visibleStatements.map((s) => s.id));
    return jobs.filter((job) =>
      job.payouts.some((payout) =>
        allowedStatementIds.has(payout.statement_id),
      ),
    );
  }, [jobs, foxSonsVisibleTechIds, visibleStatements]);

  const { mutate: createAiRun, isPending: isCreatingAiRun } =
    useCreateAiScriptRun(businessId);
  const {
    download: downloadAiWorkbook,
    isDownloading: isDownloadingAiWorkbook,
    isPreparing: isPreparingAiWorkbook,
    error: aiWorkbookDownloadError,
    isComplete: isAiWorkbookDownloadComplete,
  } = useDownloadAiScriptWorkbook(businessId);
  const { mutate: createPeriod, isPending: isCreatingPeriod } =
    useCreatePayPeriod(businessId);
  const { mutate: triggerRun, isPending: isTriggeringRun } =
    useTriggerPayrollRun(businessId, selectedPayPeriod?.id ?? null);

  // Only flips once the API reports the file still has to be built — a cached
  // workbook downloads immediately with no toast.
  useEffect(() => {
    if (!isPreparingAiWorkbook) return;

    toast.info("Preparing your Grid", {
      description:
        "Large payrolls take a moment to build. The download starts on its own.",
    });
  }, [isPreparingAiWorkbook]);

  useEffect(() => {
    if (!aiWorkbookDownloadError) return;

    toast.error("Failed to download payroll result", {
      description: getAiScriptWorkbookDownloadErrorMessage(
        aiWorkbookDownloadError,
      ),
    });
  }, [aiWorkbookDownloadError]);

  const template = business?.current_pay_period_template ?? DEFAULT_TEMPLATE;
  const { periods: rawPeriods, calendarPeriods } = useMemo(
    () =>
      buildPeriods({
        template,
        payPeriods,
        isBusinessLoading,
        businessCreatedAt: business?.created_at,
      }),
    [template, payPeriods, isBusinessLoading, business?.created_at],
  );
  const periods = useMemo(
    () => applyPayBreakdownStatusToPeriods(rawPeriods, payPeriods),
    [rawPeriods, payPeriods],
  );

  const isPeriodsLoading = isBusinessLoading || isPayPeriodsLoading;

  useEffect(() => {
    const defaultPeriod = getDefaultPayBreakdownPeriod(
      periods,
      selectedPeriodStart,
    );

    if (defaultPeriod) {
      navigate(
        {
          pathname: getPayBreakdownPath(defaultPeriod.period_start),
          search,
        },
        { replace: true },
      );
    }
  }, [navigate, periods, search, selectedPeriodStart]);

  const selectedPeriod = periods.find(
    (period) => period.period_start === selectedPeriodStart,
  );
  const selectedPeriodForCard: EnrichedPayPeriod | undefined = useMemo(() => {
    if (selectedPayPeriod) return selectedPayPeriod;
    if (!selectedPeriod) return undefined;

    return createCalculatedPayPeriod(
      dayjs(selectedPeriod.period_start),
      dayjs(selectedPeriod.period_end),
    );
  }, [selectedPayPeriod, selectedPeriod]);
  const hasActiveAiRun = hasActiveAiScriptRun(selectedPayPeriod);
  const hasActiveIcmPayrollRun = hasActiveIcmRun(selectedPayPeriod);
  const hasActiveLegacyRun =
    selectedPayPeriod?.latest_run_status === PAYROLL_RUN_STATUS.pending ||
    selectedPayPeriod?.latest_run_status === PAYROLL_RUN_STATUS.processing;
  const hasActiveRun =
    hasActiveAiRun || hasActiveIcmPayrollRun || hasActiveLegacyRun;
  const hasActiveRunRef = useRef(hasActiveRun);
  const activeAiScriptRuns = selectedPayPeriod?.active_ai_script_runs ?? [];
  // Tabs already running or queued for this period, so their control reads busy
  // and a repeat request is suppressed before it reaches the API.
  const busyRerunSheetNames = useMemo(
    () => activeAiScriptRuns.flatMap((run) => run.sheet_scope ?? []),
    [activeAiScriptRuns],
  );
  // A full-workbook run recomputes every tab, so no tab can be rerun on its own
  // while one is in flight. Keyed on a null scope, NOT an empty one: the API
  // redacts a scoped run's tab names to `[]` when they are all hidden from this
  // user, and treating that as full-workbook would let another department's rerun
  // disable this user's only control.
  const hasActiveFullAiRun = activeAiScriptRuns.some(isFullWorkbookRun);
  // Anything that makes a per-tab rerun the wrong thing to start right now. A
  // full-workbook AI run rewrites every tab, so no tab can be rerun beside it — and
  // an ICM or legacy calculation blocks too, because the AI run queue only serializes
  // AI runs. Without those, a scoped request would slip past the mutual exclusion the
  // rest of the page enforces. Scoped AI runs still queue behind each other, which is
  // the whole point, so they are deliberately not blocking.
  const hasBlockingRun =
    hasActiveFullAiRun || hasActiveIcmPayrollRun || hasActiveLegacyRun;

  // Same query key the viewer uses, so this shares its cache rather than
  // fetching the workbook a second time.
  const { data: workbookData } = useGetAiScriptSpreadsheet(
    businessId,
    aiScriptRunId,
  );
  // The groups the rerun dialog offers. Rebuilt from live workbook and approval
  // state so a unit that starts running while the dialog is open drops out.
  const rerunUnits = useMemo(
    () =>
      getRerunUnits({
        sheets: workbookData?.spreadsheet.sheets ?? [],
        approvalStatus,
        busySheetNames: busyRerunSheetNames,
      }),
    [workbookData?.spreadsheet.sheets, approvalStatus, busyRerunSheetNames],
  );
  // Stands in for the api's `any_sheet_edit_authority?` while scoped reruns are
  // off — the workbook's per-sheet `editable` is the only edit signal the client
  // has. Only ever narrows: see canRerunWholeWorkbook.
  const hasEditableSheet = useMemo(
    () =>
      (workbookData?.spreadsheet.sheets ?? []).some((sheet) => sheet.editable),
    [workbookData?.spreadsheet.sheets],
  );

  useEffect(() => {
    if (!selectedIcmOutputRunId) {
      hasActiveRunRef.current = hasActiveRun;
      return;
    }

    if (hasActiveRunRef.current && !hasActiveRun) {
      refetchStatements();
      refetchJobs();
    }
    hasActiveRunRef.current = hasActiveRun;
  }, [hasActiveRun, refetchJobs, refetchStatements, selectedIcmOutputRunId]);

  const selectedRunStatus =
    selectedPayPeriod?.latest_ai_script_run_status ?? null;
  const selectedPayrollRunStatus =
    getAiScriptPayrollRunStatus(selectedRunStatus);
  const icmLatestRunStatus = selectedPayPeriod?.latest_icm_run_status ?? null;
  const isIcmRunActionRunning =
    isCreatingPeriod || isTriggeringRun || hasActiveIcmPayrollRun;

  const isBeforeBusinessCreated =
    !!selectedPeriodStart &&
    !!business?.created_at &&
    dayjs(selectedPeriodStart).isBefore(dayjs(business.created_at), "day");
  const periodNotFound =
    !!selectedPeriodStart &&
    !isPeriodsLoading &&
    periods.length > 0 &&
    !selectedPeriod;
  const periodReady =
    !!selectedPeriodStart &&
    !isPeriodsLoading &&
    !periodNotFound &&
    !hasActiveRun;

  const hasAiScript = hasAiScriptAccess(business);
  // Who may start a whole-payroll run moves with the scoped-rerun rollout, so the
  // API decides it and reports it as `can_run_whole_payroll`; see
  // canRerunWholeWorkbook. `hasEditableSheet` adds the one term that field cannot
  // carry, which is whether they may edit part of THIS period's workbook.
  const canRerunWholePayroll = canRerunWholeWorkbook({
    business,
    hasEditableSheet,
  });
  const canStartInitialPayroll = canRunInitialPayroll(
    business,
    selectedAiRunId,
  );
  // Apply saved product settings and API-authorized roster visibility.
  const { canViewRoster, isLoading: isRosterAccessLoading } =
    useCanViewPayrollRoster(business, user);
  const hasIcmAccess = hasIcmPayrollViewerAccess(business);
  const hasPayrollScript = hasPayrollScriptAccess(business);

  const { payrollEmailsAccess, isLoading: isPayrollEmailsAccessLoading } =
    usePayrollEmailsAccess();
  const { canViewPayPlan, isLoading: isPayPlanAccessLoading } =
    usePayPlanAccess();
  const canViewPayrollEmails =
    payrollEmailsAccess !== PAYROLL_EMAILS_ACCESS.hidden;
  // PackChat has its own permission rule, which includes managers and tenant
  // users. It must not borrow the stricter PayPack send permission. The saved
  // Payroll and PayPacks selections control business access; the API also checks
  // which sheets this reviewer may read.
  const { enabled: packChatEnabled, isLoading: isPaypacksAccessLoading } =
    usePaypacksAccess();
  const canViewPackChat = mayAnswerPackQuestions(user) && packChatEnabled;
  // Also badges the PackChat tab.
  const { data: managedThreads } = useGetManagedPackThreads(
    businessId,
    selectedPeriodStart ?? null,
    { enabled: canViewPackChat },
  );
  // Three independent gates, in the house shape: WHETHER the business has the
  // feature is the saved PushPay selection, WHETHER there is anywhere to push is the
  // ServiceTitan connection, and WHO may use it is finance access plus authority
  // over the whole workbook.
  //
  // That last one matters more than usual here: the adjustments endpoints require
  // it — a read exposes every payroll line and a push touches every sheet — so
  // without it a manager, rep or tech sees a tab whose every request 403s.
  const { access: stAdjustmentsProductAccess, isLoading: isStProductsLoading } =
    useStAdjustmentsProductAccess();
  const stAdjustmentsAccess = getStAdjustmentsAccess({
    productAccess: stAdjustmentsProductAccess,
    serviceTitanEnabled: !!business?.service_titan_enabled,
    isAiScript: output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript,
    hasFullWorkbookAccess: hasFullWorkbookAuthority(business, user),
  });
  const hasStAdjustmentsAccess =
    stAdjustmentsAccess !== ST_ADJUSTMENTS_ACCESS.hidden;
  const stAdjustmentsSectionActions =
    stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.preview ? (
      <StAdjustmentsPreviewHeaderActions />
    ) : stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.enabled ? (
      <StAdjustmentsHeaderActions />
    ) : undefined;
  const hasAdditionalSection =
    canViewRoster ||
    canViewPayPlan ||
    canViewPayrollEmails ||
    canViewPackChat ||
    hasStAdjustmentsAccess;

  // Section sub-tabs only appear when there is genuinely more than one section to
  // switch between: the workbook has rendered (output.type === aiScript) AND the
  // viewer has at least one extra section — a manageable roster, visible pay plan,
  // the emails feature, PackChat, or ServiceTitan adjustments to push. An un-run
  // period shows just its "Run Payroll" empty state. ICM/legacy pages are
  // unchanged.
  //
  // PackChat counts, so anyone who can answer pay questions now gets a tab bar on a
  // rendered workbook. That is the point rather than a side effect: they have a second
  // section whether or not the business uses the other three. A technician, and a rep at
  // the business, still fall through to the bare Grid — but a rep parented to a TENANT
  // does get it, because tenant membership grants regardless of role here exactly as it
  // does in `can_manage_finances?`. See `mayAnswerPackQuestions`.
  const showSectionTabs =
    hasAiScript &&
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript &&
    hasAdditionalSection;

  const isSectionRoutingReady =
    !!selectedPeriodStart &&
    !isBusinessLoading &&
    !isPayPeriodsLoading &&
    !isRosterAccessLoading &&
    !isPayPlanAccessLoading &&
    !isPayrollEmailsAccessLoading &&
    !isPaypacksAccessLoading &&
    // Without this, a deep link to /adjustments is replaced with /grid before the
    // flag that decides the tab's availability has resolved.
    !isStProductsLoading;

  const { activeSection, setActiveSection } = usePayBreakdownSection({
    availability: {
      [PAY_BREAKDOWN_SECTION.breakdown]: true,
      [PAY_BREAKDOWN_SECTION.roster]: showSectionTabs && canViewRoster,
      [PAY_BREAKDOWN_SECTION.payPlan]: showSectionTabs && canViewPayPlan,
      [PAY_BREAKDOWN_SECTION.packs]: showSectionTabs && canViewPayrollEmails,
      [PAY_BREAKDOWN_SECTION.packChat]: showSectionTabs && canViewPackChat,
      [PAY_BREAKDOWN_SECTION.adjustments]:
        showSectionTabs && hasStAdjustmentsAccess,
    },
    isReady: isSectionRoutingReady,
  });

  // Shape the lightweight run/current version pointers once for both the
  // historical roster tab and the rerun dialog.
  const { rosterVersionNumber, currentRosterVersionNumber, rerunVersionInfo } =
    getPayBreakdownVersionContext(selectedPayPeriod, payPeriods);

  // Warm the historical tab through the same access and cache guards as reads.
  usePayrollRosterVersion(
    showSectionTabs && canViewRoster ? businessId : null,
    rosterVersionNumber ?? currentRosterVersionNumber,
  );
  const activePlanId = business?.active_icm_plan_id ?? null;
  const activePlanName = business?.active_icm_plan_name ?? null;
  const lastRunPlanId =
    selectedPayPeriod?.latest_completed_icm_run_plan_id ?? null;
  const lastRunPlanName =
    selectedPayPeriod?.latest_completed_icm_run_plan_name ?? null;
  const planMismatch =
    !!activePlanId && !!lastRunPlanId && activePlanId !== lastRunPlanId;
  const noPlanAvailable = !activePlanId && !lastRunPlanId;
  const icmRunDisabledReason = noPlanAvailable
    ? "No active ICM plan configured for this business"
    : isBeforeBusinessCreated
      ? "ICM runs can't be requested for periods before the business was created"
      : undefined;
  const { enabled: showIcmRerunControls } = useFeatureFlag(
    FEATURE_FLAG_NAMES.enable_icm_reruns,
  );

  const latestScriptRunStatus = selectedPayPeriod?.latest_run_status ?? null;
  const runTarget = getPayBreakdownRunTarget({
    selectedPeriod,
    selectedPeriodForCard,
    latestScriptRunStatus,
    latestIcmRunStatus: icmLatestRunStatus,
    hasActiveRun,
    isLocked,
    hasAiScript,
    hasIcmAccess,
    hasPayrollScript,
    icmRunDisabledReason,
    canRerunWholePayroll,
    canStartInitialPayroll,
    hasRerunUnits: rerunUnits.length > 0,
  });

  const handleTriggerAiRun = (
    runOptions?: AiScriptRerunRunOptions,
    chosenSheetNames?: string[],
  ) => {
    if (!selectedPeriod || isCreatingAiRun || isLocked) return;
    // A per-tab rerun may be QUEUED behind another SCOPED run — they legitimately land
    // minutes apart, and that is the feature. Anything else in flight blocks, checked
    // here as well as at the control, because a dialog can sit open across a poll that
    // starts one.
    if (hasBlockingRun && chosenSheetNames?.length) return;
    // The dialog owns the scope: a list of tab names for a scoped rerun, or
    // nothing at all for the whole workbook.
    const sheetNames = chosenSheetNames?.length ? chosenSheetNames : null;
    if (hasActiveRun && !sheetNames) return;

    createAiRun(
      {
        startDate: selectedPeriod.period_start,
        endDate: selectedPeriod.period_end,
        aiScriptVersionId: runOptions?.aiScriptVersionId ?? null,
        payrollRosterId: runOptions?.payrollRosterId ?? null,
        sheetNames,
      },
      {
        onSuccess: (response) => {
          const label =
            sheetNames && sheetNames.length > 1
              ? `${sheetNames.length} tabs`
              : (sheetNames?.[0] ?? "Payroll");
          if (response.coalesced) {
            toast.info(`${label} is already running or queued`);
          } else if (response.run.status === "pending" && hasActiveRun) {
            toast.success(
              `${label} queued — it will start when the current run finishes`,
            );
          } else {
            toast.success(
              sheetNames ? `${label} rerun started` : "Payroll run started",
            );
          }
        },
        onError: (error: unknown) => {
          toast.error("Failed to start payroll run", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  const handleRequestAiRerun = () => {
    // Deliberately not gated on hasActiveRun: the dialog can still offer a
    // scoped rerun that queues behind whatever is running, and it explains the
    // wait itself.
    if (!selectedPeriod || isCreatingAiRun || isLocked) return;

    setAiScriptRerunConfirmOpen(true);
  };

  const submitIcmRun = (icmPlanId?: string) => {
    if (!selectedPeriodForCard) return;

    const callbacks = {
      onSuccess: () => toast.success("ICM calculation started"),
      onError: (error: unknown) =>
        toast.error("Failed to start calculation", {
          description: getErrorMessage(error),
        }),
    };

    if (!selectedPeriodForCard.existsInDb) {
      createPeriod(
        {
          start_date: selectedPeriodForCard.start_date,
          end_date: selectedPeriodForCard.end_date,
        },
        callbacks,
      );
      return;
    }

    triggerRun(
      {
        run_type: PAYROLL_RUN_TYPE.icm,
        ...(icmPlanId ? { icm_plan_id: icmPlanId } : {}),
      },
      callbacks,
    );
  };

  const handleRunIcm = () => {
    if (!selectedPeriodForCard) return;

    if (planMismatch) {
      setPlanPickerOpen(true);
      return;
    }

    if (!activePlanId && lastRunPlanId) {
      submitIcmRun(lastRunPlanId);
      return;
    }

    submitIcmRun();
  };

  const handleDownloadAiWorkbook = (options?: SelectedWorkbookExport) => {
    if (output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript) return;

    const filename =
      generatedWorkbookMetadata?.filename ??
      DEFAULT_AI_SCRIPT_WORKBOOK_FILENAME;
    downloadAiWorkbook({
      runId: output.runId,
      filename: options
        ? filename.replace(/\.xlsx$/i, `.${options.exportFormat}`)
        : filename,
      ...options,
    });
  };

  const handleDownloadLegacyRun = () => {
    if (
      output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript ||
      !output.run.result_file_url
    ) {
      return;
    }

    downloadFile(output.run.result_file_url, getLegacyRunFilename(output.run));
  };

  const handleRunLegacyScript = () => {
    if (
      !selectedPeriodForCard ||
      hasActiveRun ||
      isTriggeringRun ||
      isCreatingPeriod
    ) {
      return;
    }

    const callbacks = {
      onSuccess: () => toast.success("Payroll request submitted"),
      onError: (error: unknown) =>
        toast.error("Failed to start payroll run", {
          description: getErrorMessage(error),
        }),
    };

    if (!selectedPeriodForCard.existsInDb) {
      createPeriod(
        {
          start_date: selectedPeriodForCard.start_date,
          end_date: selectedPeriodForCard.end_date,
        },
        callbacks,
      );
      return;
    }

    triggerRun({ run_type: PAYROLL_RUN_TYPE.legacyScript }, callbacks);
  };

  const handleRunPreferredPayroll = () => {
    if (!runTarget.canRunPeriod) return;

    switch (runTarget.type) {
      case PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript:
        if (output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript) {
          handleRequestAiRerun();
          return;
        }
        handleTriggerAiRun();
        return;
      case PAY_BREAKDOWN_RUN_TARGET_TYPE.icm:
        handleRunIcm();
        return;
      case PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript:
        handleRunLegacyScript();
        return;
    }
  };

  const handleOpenExplanationChat = () => {
    if (activeSection === PAY_BREAKDOWN_SECTION.packChat) {
      setSelectionContext(null);
    }
    setSelectedJob(null);
    closeManualEdits();
    setIsCommentsOpen(false);
    setIsExplanationChatOpen(true);
  };

  const handleAskAiAboutCell = (context: AiScriptChatSelectionContext) => {
    setSelectionContext(context);
    setPendingQuestion(CELL_FIGURE_QUESTION);
    handleOpenExplanationChat();
  };

  const handleOpenComments = () => {
    setSelectedJob(null);
    closeManualEdits();
    setIsExplanationChatOpen(false);
    setIsCommentsOpen(true);
  };

  const handleOpenManualEdits = () => {
    setSelectedJob(null);
    setIsCommentsOpen(false);
    setIsExplanationChatOpen(false);
    openManualEditsList();
  };

  // Opened from the formula-bar "View edit" action: a cell-scoped sidebar
  // focused on that cell, which closes once the user clicks off it (below).
  const handleViewEditedCell = (selection: IAiScriptEditedCellSelection) => {
    setSelectedJob(null);
    setIsExplanationChatOpen(false);
    setIsCommentsOpen(false);
    openManualEdit(selection);
  };

  // Scroll/select the edited cell in the grid.
  const handleGoToCell = useCallback((cell: IAiSpreadsheetCellLocation) => {
    spreadsheetViewerRef.current?.goToCell(cell.sheet_id, cell.row, cell.col);
  }, []);

  const rerunAction = getPayBreakdownRerunAction({
    runTarget,
    hasActiveRun,
    showIcmRerunControls,
    isCreatingAiRun,
    isCreatingPeriod,
    isTriggeringRun,
    isIcmRunActionRunning,
    onRunPreferredPayroll: handleRunPreferredPayroll,
  });

  const latestRunAt = getPayBreakdownLatestRunAt(output, selectedPayPeriod);
  const calculationState = getPayBreakdownCalculationState({
    hasSelectedPeriod: !!selectedPeriod,
    hasActiveRun,
    hasOutput: output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.none,
    activeRunAffectsVisibleOutput:
      hasActiveIcmPayrollRun ||
      hasActiveLegacyRun ||
      hasActiveAiRunAffectingVisibleSheets(activeAiScriptRuns),
  });
  const showInitialCalculationState =
    calculationState === PAY_BREAKDOWN_CALCULATION_STATE.initial;
  const showLegacyDetailLoading =
    periodReady && shouldFetchLegacyDetail && isLegacyDetailLoading;
  const showNoRunState =
    periodReady &&
    !isLegacyDetailLoading &&
    !hasActiveRun &&
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.none;
  const showFailedAiRun =
    showNoRunState &&
    selectedRunStatus === PAYROLL_RUN_STATUS.failed &&
    runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript;
  const showNoAiRunYet =
    showNoRunState &&
    selectedRunStatus !== PAYROLL_RUN_STATUS.failed &&
    runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript &&
    !!selectedPeriod;
  const showNoIcmRunYet =
    showNoRunState &&
    runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm &&
    !!selectedPeriodForCard;
  const showNoLegacyRunYet =
    showNoRunState &&
    runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript &&
    !!selectedPeriodForCard;
  const showIcmOutputLoading =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm && isStatementsLoading;
  const showIcmNoResults =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm &&
    !isStatementsLoading &&
    !!statements &&
    statements.length === 0;
  const showIcmOutput =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm &&
    !isStatementsLoading &&
    !!statements &&
    statements.length > 0;

  // Only to mark the recipients table. The questions live on PackChat.
  const packChatOpenByTechnician = useMemo(
    () =>
      new Set(
        (managedThreads?.threads ?? [])
          .filter((thread) => thread.status === PACK_CHAT_STATUS.open)
          .map((thread) => thread.technician.id),
      ),
    [managedThreads?.threads],
  );

  const isSidebarOpen =
    !!selectedJob ||
    isExplanationChatOpen ||
    isManualEditsOpen ||
    isCommentsOpen ||
    isSectionSidebarOpen;
  const registerSectionSidebarClose = useCallback(
    (handler: (() => void) | null) => {
      sectionSidebarCloseRef.current = handler;
    },
    [],
  );

  return (
    // Mounted for every section, not just PackChat's: swapping a provider in and
    // out per active tab would change the element type at the top of the page and
    // remount everything under it — including the Grid, which is kept mounted on
    // purpose so a workbook and its selection survive tab switches. A section that
    // needs header controls adds its provider here and its actions below.
    <PackChatSectionProvider>
      {/* Idled off-tab by a null period: every query underneath is gated on
          business and period, so this can stay mounted — which it must, or the
          Grid remounts on every tab switch. */}
      <PacksSectionProvider
        businessId={businessId}
        periodStart={
          activeSection === PAY_BREAKDOWN_SECTION.packs
            ? (selectedPeriodStart ?? null)
            : null
        }
        payrollEmailsAccess={payrollEmailsAccess}
        awaitingReplyTechnicianIds={packChatOpenByTechnician}
      >
        {/* The real run stays attached off-tab so an active push can finish its
            status watch. `enabled` idles only the view data until PushPay returns. */}
        <StAdjustmentsSectionProvider
          businessId={businessId}
          access={stAdjustmentsAccess}
          // Keep the run identity in preview mode so a period change resets its
          // open technician pane. The provider gates the live push watcher.
          runId={aiScriptRunId}
          enabled={activeSection === PAY_BREAKDOWN_SECTION.adjustments}
          // Same fallback the Roster tab uses: the run's snapshot, or the live
          // published version when a run predates snapshots.
          rosterVersionNumber={
            rosterVersionNumber ?? currentRosterVersionNumber
          }
        >
          <SimpleSidebarLayout
            open={isSidebarOpen}
            onOpenChange={(open) => {
              if (open) return;
              sectionSidebarCloseRef.current?.();
              setSectionSidebarOpen(false);
              setSelectedJob(null);
              setIsExplanationChatOpen(false);
              setIsCommentsOpen(false);
              closeManualEdits();
            }}
          >
            <SimpleSidebarContent
              removeScrollArea={activeSection === PAY_BREAKDOWN_SECTION.roster}
            >
              <Stack
                fullWidth
                className={cn("min-w-0 [contain:inline-size]", {
                  "h-full min-h-0":
                    activeSection === PAY_BREAKDOWN_SECTION.roster,
                })}
                gap="md"
              >
                <Stack
                  align="stretch"
                  fullWidth
                  className="mx-auto max-w-[1680px] shrink-0 px-8 pt-6 [contain:inline-size]"
                  gap="md"
                >
                  <PayBreakdownHeader
                    selectedPeriod={selectedPeriod}
                    latestRunAt={latestRunAt}
                    approvalStatus={approvalStatus}
                    approvalActions={{
                      pending: sheetApprovalActions.pendingApproval,
                      onChange: sheetApprovalActions.handleChange,
                    }}
                    calculationState={calculationState}
                    output={output}
                    aiWorkbookDownload={{
                      isDownloading: isDownloadingAiWorkbook,
                      onDownload: () => setIsWorkbookExportOpen(true),
                    }}
                    onOpenExplanationChat={handleOpenExplanationChat}
                    tabs={
                      showSectionTabs ? (
                        <PayBreakdownSectionTabs
                          activeSection={activeSection}
                          showRoster={canViewRoster}
                          showPayPlan={canViewPayPlan}
                          payrollEmailsAccess={payrollEmailsAccess}
                          openThreadCount={managedThreads?.open_count ?? 0}
                          showPackChat={canViewPackChat}
                          stAdjustmentsAccess={stAdjustmentsAccess}
                          onSectionChange={setActiveSection}
                        />
                      ) : undefined
                    }
                    manualEdits={
                      activeSection === PAY_BREAKDOWN_SECTION.breakdown
                        ? {
                            reviewCount: manualEditsToReview,
                            onOpen: handleOpenManualEdits,
                          }
                        : undefined
                    }
                    comments={
                      activeSection === PAY_BREAKDOWN_SECTION.breakdown &&
                      aiScriptRunId
                        ? {
                            openCount: openCommentCount,
                            onOpen: handleOpenComments,
                          }
                        : undefined
                    }
                    rerunAction={rerunAction}
                    approvalRequest={
                      aiScriptRunId && canRequestApprovals ? (
                        <RequestApprovalAction
                          businessId={businessId}
                          runId={aiScriptRunId}
                          state={sheetApprovalState}
                          approvalStatus={approvalStatus}
                        />
                      ) : undefined
                    }
                    periodPicker={{
                      periods,
                      calendarPeriods,
                      selectedPeriodStart,
                      isLoading: isPeriodsLoading,
                    }}
                    lock={
                      showLockPayPeriod && selectedPayPeriod
                        ? { businessId, period: selectedPayPeriod }
                        : undefined
                    }
                    // Whichever section is showing owns the header's action slot.
                    // Each one passes a component that reads its own section
                    // provider, mounted above this header.
                    sectionActions={
                      activeSection === PAY_BREAKDOWN_SECTION.packChat ? (
                        <PackChatHeaderActions />
                      ) : activeSection === PAY_BREAKDOWN_SECTION.packs ? (
                        <PacksHeaderActions />
                      ) : activeSection ===
                        PAY_BREAKDOWN_SECTION.adjustments ? (
                        stAdjustmentsSectionActions
                      ) : undefined
                    }
                    // Sections that bring their own header actions replace the
                    // workbook ones wholesale rather than queueing behind them.
                    hideDefaultActions={
                      activeSection === PAY_BREAKDOWN_SECTION.packs ||
                      activeSection === PAY_BREAKDOWN_SECTION.packChat ||
                      activeSection === PAY_BREAKDOWN_SECTION.adjustments
                    }
                    showExplanationChatInMenu={
                      activeSection === PAY_BREAKDOWN_SECTION.packChat
                    }
                  />

                  {!selectedPeriodStart && (
                    <Typography variant="bodyW2" textColor="secondary">
                      Select a period to view payroll details.
                    </Typography>
                  )}
                  {periodNotFound && (
                    <Typography variant="bodyW2" textColor="secondary">
                      No payroll details are available for this period.
                    </Typography>
                  )}
                </Stack>

                {/* Grid section — the workbook / run output. Kept mounted
              (hidden, not unmounted) when another sub-tab is active so the
              workbook and its selection survive tab switches. */}
                <Stack
                  fullWidth
                  className={cn("min-w-0", {
                    hidden: activeSection !== PAY_BREAKDOWN_SECTION.breakdown,
                  })}
                >
                  {(isBeforeBusinessCreated || showLegacyDetailLoading) && (
                    <Stack
                      fullWidth
                      className="mx-auto max-w-[1680px] px-8 [contain:inline-size]"
                      gap="md"
                    >
                      {isBeforeBusinessCreated && (
                        <FeedbackBanner variant="warning">
                          <Typography variant="bodyW3">
                            This period is before this business was created.
                          </Typography>
                          <Typography variant="bodyW2" textColor="secondary">
                            Existing payroll data is still visible, but new ICM
                            runs can't be requested for this period.
                          </Typography>
                        </FeedbackBanner>
                      )}
                      {showLegacyDetailLoading && (
                        <LoadingSpinner
                          className="h-[400px]"
                          size="lg"
                          color="primary"
                        />
                      )}
                    </Stack>
                  )}

                  {output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
                    <PayBreakdownPaneShell
                      gap="none"
                      className={PAYROLL_GRID_CONTAINER_CLASS_NAME}
                    >
                      <PayBreakdownSpreadsheetViewer
                        key={output.runId}
                        ref={spreadsheetViewerRef}
                        businessId={businessId}
                        runId={output.runId}
                        fillHeight
                        isGlobalSearchEnabled={
                          activeSection === PAY_BREAKDOWN_SECTION.breakdown
                        }
                        onSelectionContextChange={setSelectionContext}
                        onAskAiAboutCell={handleAskAiAboutCell}
                        onViewEditedCell={handleViewEditedCell}
                        onEditedCellSelectionChange={
                          handleEditedCellSelectionChange
                        }
                        sidebarOpen={isSidebarOpen}
                        onMaximizedChange={setIsSheetMaximized}
                        isLocked={
                          isLocked ||
                          hasActiveRun ||
                          isCreatingAiRun ||
                          isTriggeringRun
                        }
                        approvalStatus={approvalStatus}
                        onSheetApprovalChange={
                          sheetApprovalActions.handleChange
                        }
                        pendingSheetApproval={
                          sheetApprovalActions.pendingApproval
                        }
                      />
                    </PayBreakdownPaneShell>
                  )}

                  {output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
                    <Stack
                      fullWidth
                      className="mx-auto max-w-[1680px] px-8 pb-8 [contain:inline-size]"
                      gap="md"
                    >
                      {showInitialCalculationState && (
                        <PayBreakdownCalculationLoadingState />
                      )}
                      {showIcmOutputLoading && (
                        <LoadingSpinner
                          className="h-[400px]"
                          size="lg"
                          color="primary"
                        />
                      )}
                      {showIcmNoResults && (
                        <Stack
                          fullWidth
                          justify="center"
                          align="center"
                          className="h-[300px]"
                        >
                          <Typography variant="titleW3">
                            No commissions were earned for this period.
                          </Typography>
                        </Stack>
                      )}
                      {showIcmOutput && (
                        <>
                          <IcmPageSummaryCard
                            statements={visibleStatements}
                            jobs={visibleJobs}
                          />
                          <IcmPageBreakdownCard
                            periodStart={selectedPeriodStart}
                            statements={visibleStatements}
                            jobs={visibleJobs}
                            onSelectJob={setSelectedJob}
                          />
                        </>
                      )}
                      {output.type ===
                        PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript && (
                        <LegacyScriptOutputState
                          run={output.run}
                          onDownload={handleDownloadLegacyRun}
                        />
                      )}
                      {showNoAiRunYet && (
                        <AiScriptRunState
                          title="Payroll has not been run for this period."
                          action="run"
                          isLoading={isCreatingAiRun || hasActiveRun}
                          disabledReason={
                            isLocked ? LOCKED_RERUN_REASON : undefined
                          }
                          onAction={handleRunPreferredPayroll}
                        />
                      )}
                      {showFailedAiRun && (
                        <AiScriptRunState
                          title="Payroll run failed."
                          action="run"
                          status={selectedPayrollRunStatus}
                          isLoading={isCreatingAiRun || hasActiveRun}
                          disabledReason={
                            isLocked ? LOCKED_RERUN_REASON : undefined
                          }
                          onAction={handleRunPreferredPayroll}
                        />
                      )}
                      {showNoIcmRunYet && (
                        <Stack
                          fullWidth
                          justify="center"
                          align="center"
                          className="h-[300px]"
                          gap="md"
                        >
                          <Typography variant="titleW3">
                            Payroll has not been run for this period.
                          </Typography>
                          <PayPeriodCard
                            period={selectedPeriodForCard}
                            mode="icm"
                            disabledReason={icmRunDisabledReason}
                          />
                        </Stack>
                      )}
                      {showNoLegacyRunYet && (
                        <Stack
                          fullWidth
                          justify="center"
                          align="center"
                          className="h-[300px]"
                          gap="md"
                        >
                          <Typography variant="titleW3">
                            Payroll has not been run for this period.
                          </Typography>
                          <PayPeriodCard period={selectedPeriodForCard} />
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Stack>

                {showSectionTabs &&
                  canViewRoster &&
                  activeSection === PAY_BREAKDOWN_SECTION.roster &&
                  businessId && (
                    <PayBreakdownRosterTab
                      businessId={businessId}
                      runVersionNumber={rosterVersionNumber}
                      currentVersionNumber={currentRosterVersionNumber}
                    />
                  )}
                {showSectionTabs &&
                  canViewPayPlan &&
                  activeSection === PAY_BREAKDOWN_SECTION.payPlan &&
                  businessId &&
                  aiScriptRunId && (
                    <PayBreakdownPayPlanTab
                      businessId={businessId}
                      runId={aiScriptRunId}
                      canEditRules={business?.can_edit_payroll_rules === true}
                    />
                  )}
                {showSectionTabs &&
                  activeSection === PAY_BREAKDOWN_SECTION.packs && (
                    <PayBreakdownEmailsTab />
                  )}
                {showSectionTabs &&
                  activeSection === PAY_BREAKDOWN_SECTION.packChat && (
                    <PayBreakdownPackChatTab
                      businessId={businessId}
                      periodStart={selectedPeriodStart}
                      // The workbook this page is showing, so the pack beside a
                      // conversation is the one the Grid tab has open rather than
                      // whichever run the question happened to be raised on.
                      runId={aiScriptRunId}
                      isLocked={isLocked}
                    />
                  )}
                {showSectionTabs &&
                  activeSection === PAY_BREAKDOWN_SECTION.adjustments && (
                    <PayBreakdownAdjustmentsTab />
                  )}
              </Stack>
            </SimpleSidebarContent>

            <SimpleSidebar
              // In fullscreen the grid overlay (z-[900]) starts at top-0 and covers
              // the app header, so the sidebar sits flush (top-0, full height) and
              // above the overlay. z-[950] matches the viewer overlay convention.
              className={cn({
                "top-0 z-[950] h-screen": isSheetMaximized,
              })}
            >
              {activeSection === PAY_BREAKDOWN_SECTION.packs && (
                <PacksSidebar
                  onOpenChange={setSectionSidebarOpen}
                  registerClose={registerSectionSidebarClose}
                />
              )}
              {activeSection === PAY_BREAKDOWN_SECTION.adjustments &&
                stAdjustmentsAccess !== ST_ADJUSTMENTS_ACCESS.hidden && (
                  <StAdjustmentsSidebar
                    onOpenChange={setSectionSidebarOpen}
                    registerClose={registerSectionSidebarClose}
                  />
                )}
              {selectedJob && (
                <IcmJobSidebar
                  job={selectedJob}
                  periodStart={selectedPeriodStart}
                  onClose={() => setSelectedJob(null)}
                />
              )}
              {isExplanationChatOpen &&
                output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript &&
                (appEnv === "demo" ? (
                  <DemoAiScriptExplanationChatSidebar
                    key={output.runId}
                    selectionContext={selectionContext}
                    pendingQuestion={pendingQuestion}
                    onQuestionPrepared={() => setPendingQuestion(null)}
                    onClearSelectionContext={() =>
                      spreadsheetViewerRef.current?.clearSelection()
                    }
                    onClose={() => setIsExplanationChatOpen(false)}
                  />
                ) : (
                  <AiScriptExplanationChatSidebar
                    key={output.runId}
                    businessId={businessId}
                    runId={output.runId}
                    selectionContext={selectionContext}
                    pendingQuestion={pendingQuestion}
                    onQuestionPrepared={() => setPendingQuestion(null)}
                    onClearSelectionContext={() =>
                      spreadsheetViewerRef.current?.clearSelection()
                    }
                    onClose={() => setIsExplanationChatOpen(false)}
                  />
                ))}
              {isCommentsOpen &&
                output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
                  <CellCommentsSidebar
                    key={output.runId}
                    businessId={businessId}
                    runId={output.runId}
                    onGoToThread={({ placement }) => {
                      if (
                        placement.sheet_id &&
                        placement.row != null &&
                        placement.col != null
                      ) {
                        spreadsheetViewerRef.current?.openCommentsAt(
                          placement.sheet_id,
                          placement.row,
                          placement.col,
                        );
                      }
                    }}
                    onClose={() => setIsCommentsOpen(false)}
                  />
                )}
              {isManualEditsOpen &&
                output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
                  <AiScriptPersistentEditsSidebar
                    key={output.runId}
                    businessId={businessId}
                    runId={output.runId}
                    isLocked={isLocked}
                    isRunning={
                      hasActiveRun || isCreatingAiRun || isTriggeringRun
                    }
                    focusEdit={focusedEdit}
                    onShowAll={showAllManualEdits}
                    onFocusEdit={focusManualEdit}
                    onGoToCell={handleGoToCell}
                    onClose={closeManualEdits}
                    onWorkbookMutated={() =>
                      spreadsheetViewerRef.current?.resetHistory()
                    }
                  />
                )}
            </SimpleSidebar>

            {showIcmRerunControls && (
              <IcmPagePlanMismatchDialog
                open={planPickerOpen}
                onOpenChange={setPlanPickerOpen}
                lastRunPlanId={lastRunPlanId}
                lastRunPlanName={lastRunPlanName}
                activePlanId={activePlanId}
                activePlanName={activePlanName}
                onUsePreviousPlan={submitIcmRun}
                onUseCurrentPlan={submitIcmRun}
              />
            )}
            {isWorkbookExportOpen &&
              output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
                <WorkbookExportDialog
                  key={`${businessId}-${output.runId}`}
                  sheets={workbookData?.spreadsheet.sheets ?? []}
                  isDownloading={isDownloadingAiWorkbook}
                  isPreparing={isPreparingAiWorkbook}
                  isComplete={isAiWorkbookDownloadComplete}
                  error={
                    aiWorkbookDownloadError
                      ? getAiScriptWorkbookDownloadErrorMessage(
                          aiWorkbookDownloadError,
                        )
                      : undefined
                  }
                  onClose={() => setIsWorkbookExportOpen(false)}
                  onExport={handleDownloadAiWorkbook}
                />
              )}
            <AiScriptRerunConfirmationDialog
              open={aiScriptRerunConfirmOpen}
              onOpenChange={setAiScriptRerunConfirmOpen}
              onConfirm={handleTriggerAiRun}
              periodStart={selectedPeriod?.period_start}
              periodEnd={selectedPeriod?.period_end}
              versionInfo={rerunVersionInfo}
              // Built from live workbook + approval state, so a unit that starts
              // running while the dialog sits open drops out of the offer.
              units={rerunUnits}
              canRerunEverything={canRerunWholePayroll}
              // A tab can be queued behind a run already in flight, so the dialog can
              // promise a wait rather than guess at one.
              hasRunInProgress={activeAiScriptRuns.length > 0}
              // A full-workbook run has a null scope, so it contributes no busy tab
              // names — an already-open dialog would otherwise stay confirmable and
              // queue a scoped rerun behind a run about to rewrite those tabs anyway.
              isBlockedByOtherRun={hasBlockingRun}
            />
            <ApprovalRemovalDialog
              target={sheetApprovalActions.removalTarget}
              isSubmitting={sheetApprovalActions.isPending}
              onOpenChange={sheetApprovalActions.setRemovalOpen}
              onConfirm={sheetApprovalActions.confirmRemoval}
            />
          </SimpleSidebarLayout>
        </StAdjustmentsSectionProvider>
      </PacksSectionProvider>
    </PackChatSectionProvider>
  );
};

const PayBreakdownRoute = () => {
  const { user } = useAuth();

  if (isTech(user?.role)) {
    return <TechnicianPayrollPage />;
  }

  return <PayBreakdownPage />;
};

export default PayBreakdownRoute;
